import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../api/api";
import { APIError } from "../../constants/types";
import { ERROR_MESSAGES } from "../../constants/enums";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { INoPolicyInsuranceReports } from "../../views/tasks/InsuranceReports/insuranceReport.types";
import { uploadFile } from "../../features/FileUpload/utils";
import { removeWhitespaces } from "../../helpers";

// Fetch paginated no-policy insurance reports list
export const fetchNoPolicyInsuranceReportsList = createAsyncThunk<
  { data: INoPolicyInsuranceReports[]; totalRecords: number },
  {
    pageNumber: number;
    rowsInPerPage: number;
    limit: number;
    status: string;
    date: IDataTableV2DateState["dates"];
    searchValue: IDatatableV2AdvancedSearchFilter;
    compliantStatus: string;
    leadId?: string;
  },
  { rejectValue: APIError }
>(
  "insuranceReports/fetchNoPolicyInsuranceReportsList",
  async (params, { rejectWithValue }) => {
    try {
      let url = `/policy/get-no-policy-driver-insurance-report?pageNo=${params.pageNumber}&pageLimit=${params.limit}&itemPerPage=${params.rowsInPerPage}&from_date=${params.date.fromDate}&to_date=${params.date.toDate}`;
      if (params.searchValue.length > 0) {
        url += `&advanceFilter=${JSON.stringify(params.searchValue)}`;
      }
      if (params.status !== "-1") {
        url += "&column=request_status&value=" + params.status;
      }
      if (params.compliantStatus !== "-1") {
        url += "&status=" + params.compliantStatus;
      }
      if (params.leadId) {
        url += "&lead_id=" + params.leadId;
      }
      const res = await api.get(url);
      return { data: res.data.data, totalRecords: res.data.totalRecords };
    } catch (err: any) {
      return rejectWithValue(err);
    }
  },
);

// Fetch single no-policy insurance report
export const fetchNoPolicyInsuranceReport = createAsyncThunk<
  INoPolicyInsuranceReports,
  { licenseNumber: string; incrementalNumber: string },
  { rejectValue: APIError }
>(
  "insuranceReports/fetchNoPolicyInsuranceReport",
  async ({ licenseNumber, incrementalNumber }, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/policy/get-no-policy-driver-insurance-report?license_number=${licenseNumber}&incremental_number=${incrementalNumber}`,
      );
      const data: INoPolicyInsuranceReports[] = res.data.data;
      if (data.length > 0) {
        return data[0];
      }
      return rejectWithValue({
        message: ERROR_MESSAGES.POLICY_NOT_FOUND,
        status: 404,
      });
    } catch (err: any) {
      return rejectWithValue(err);
    }
  },
);

// Upsert no-policy driver insurance report
export const upsertNoPolicyInsuranceReport = createAsyncThunk<
  { data: INoPolicyInsuranceReports },
  {
    report: INoPolicyInsuranceReports;
    file: any | null;
    fileTwo: any | null;
  },
  { rejectValue: APIError }
>(
  "insuranceReports/upsertNoPolicyInsuranceReport",
  async ({ report, file, fileTwo }, { rejectWithValue }) => {
    try {
      const asPayload = {
        license_number: report.license_number
          ? report.license_number.replace(/\s+/g, "")
          : null,
      };
      const path = await uploadFile(
        file,
        "NO_POLICY",
        report.file_upload || "",
        asPayload,
      );
      const pathTwo = await uploadFile(
        fileTwo,
        "NO_POLICY",
        report.file_upload_two || "",
        asPayload,
      );
      const res = await api.post(
        "/policy/edit-no-policy-driver-insurance-report",
        {
          ...report,
          file_upload: path,
          file_upload_two: pathTwo,
          license_number: removeWhitespaces(report.license_number),
        },
      );
      return { data: res.data.data };
    } catch (err: any) {
      return rejectWithValue(err);
    }
  },
);
